import { useEffect, useState, type FormEvent } from "react";
import QRCode from "qrcode";
import {
  useDisablePin,
  useMfaDisable,
  useMfaEnable,
  useMfaSetup,
  useRegenerateRecoveryCodes,
  useSetPin,
  type MeResponse,
} from "../api/hooks";
import { useBranding } from "../api/hooks";
import { resolveAppName } from "../lib/branding";
import { cleanSecondFactorInput, recoveryCodesText } from "../lib/second-factor";
import { Button, Card, Chip, ErrorNotice, Field, Input, Notice } from "./ui";

/**
 * Seguridad de la cuenta propia: verificación en dos pasos (app de autenticación
 * + códigos de recuperación) y, solo para conductores, PIN de acceso.
 *
 * Es OPCIONAL y personal: cada persona activa el suyo. Nadie puede activarlo ni
 * ver los códigos de recuperación de otra (un administrador solo puede quitarlo
 * desde Usuarios si alguien pierde el móvil).
 */

// ── Códigos de recuperación (se muestran UNA vez) ────────────────────────────

function RecoveryCodesDisplay({
  codes,
  email,
  onDone,
}: {
  codes: string[];
  email: string;
  onDone: () => void;
}) {
  const branding = useBranding();
  const appName = resolveAppName(branding.data?.appName);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const text = recoveryCodesText(codes, appName, email);

  const copy = (): void => {
    void navigator.clipboard.writeText(codes.join("\n")).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  const download = (): void => {
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "codigos-de-recuperacion.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <Notice tone="warn">
        <p className="font-semibold">Guarda ahora tus códigos de recuperación: no se volverán a mostrar.</p>
        <p className="mt-1">
          Si pierdes el móvil, cada código te deja entrar <strong>una sola vez</strong>. Guárdalos en un gestor de
          contraseñas o impresos en un lugar seguro. Ni siquiera un administrador puede verlos.
        </p>
      </Notice>
      <ul
        className="grid grid-cols-2 gap-2 rounded-lg bg-ink-900 p-3 sm:grid-cols-3"
        aria-label="Códigos de recuperación"
      >
        {codes.map((code) => (
          <li key={code} className="ident text-center text-sm text-ink-50">
            {code}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button onClick={copy}>{copied ? "Copiados" : "Copiar todos"}</Button>
        <Button onClick={download}>Descargar .txt</Button>
      </div>
      <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
        <input type="checkbox" checked={saved} onChange={(event) => setSaved(event.target.checked)} />
        Ya los he guardado en un lugar seguro
      </label>
      <Button variant="primary" disabled={!saved} onClick={onDone}>
        Hecho
      </Button>
    </div>
  );
}

// ── Pedir un código (app o recuperación) para una acción sensible ────────────

function CodeConfirmForm({
  title,
  description,
  submitLabel,
  danger,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  title: string;
  description: string;
  submitLabel: string;
  danger?: boolean;
  pending: boolean;
  error: unknown;
  onSubmit: (code: string) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState("");
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    onSubmit(cleanSecondFactorInput(code));
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-ink-200 p-3 dark:border-ink-700">
      <p className="text-sm font-semibold text-ink-800 dark:text-ink-100">{title}</p>
      <p className="text-xs text-ink-600 dark:text-ink-300">{description}</p>
      <Field label="Código de verificación" htmlFor="mfa-confirm-code" hint="De tu app o un código de recuperación.">
        <Input
          id="mfa-confirm-code"
          autoComplete="one-time-code"
          autoFocus
          required
          maxLength={24}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          className="tracking-widest"
        />
      </Field>
      {error ? <ErrorNotice error={error} /> : null}
      <div className="flex gap-2">
        <Button type="submit" variant={danger ? "danger" : "primary"} disabled={pending || code.length < 6}>
          {pending ? "Comprobando…" : submitLabel}
        </Button>
        <Button onClick={onCancel}>Cancelar</Button>
      </div>
    </form>
  );
}

// ── MFA con app de autenticación ─────────────────────────────────────────────

type MfaStep = "idle" | "setup" | "codes" | "regenerate" | "disable";

function MfaSection({ user }: { user: MeResponse }) {
  const setup = useMfaSetup();
  const enable = useMfaEnable();
  const disable = useMfaDisable();
  const regenerate = useRegenerateRecoveryCodes();

  const [step, setStep] = useState<MfaStep>("idle");
  const [secret, setSecret] = useState("");
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[]>([]);

  const remaining = user.recoveryCodesRemaining ?? 0;
  const hasPin = user.pinEnabled === true;

  useEffect(() => {
    if (!setup.data) return;
    let cancelled = false;
    void QRCode.toDataURL(setup.data.otpauthUrl, { width: 200, margin: 1 }).then((url) => {
      if (!cancelled) setQr(url);
    });
    return () => {
      cancelled = true;
    };
  }, [setup.data]);

  const start = (): void => {
    setCode("");
    setQr(null);
    setup.mutate(undefined, {
      onSuccess: (result) => {
        setSecret(result.secret);
        setStep("setup");
      },
    });
  };

  const confirm = (event: FormEvent): void => {
    event.preventDefault();
    enable.mutate(
      { secret, code: cleanSecondFactorInput(code) },
      {
        onSuccess: (result) => {
          setCodes(result.recoveryCodes);
          setSecret("");
          setQr(null);
          setStep("codes");
        },
      },
    );
  };

  return (
    <section aria-labelledby="mfa-title" className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 id="mfa-title" className="text-sm font-semibold text-ink-800 dark:text-ink-100">
          Verificación en dos pasos (app de autenticación)
        </h3>
        {user.mfaEnabled ? <Chip tone="ok">Activada</Chip> : <Chip tone="neutral">Sin activar</Chip>}
      </div>

      {step === "codes" ? (
        <RecoveryCodesDisplay codes={codes} email={user.email} onDone={() => { setCodes([]); setStep("idle"); }} />
      ) : null}

      {step === "regenerate" && user.mfaEnabled ? (
        <CodeConfirmForm
          title="Generar nuevos códigos de recuperación"
          description="Los códigos anteriores dejarán de valer. Confirma con un código de tu app."
          submitLabel="Generar códigos nuevos"
          pending={regenerate.isPending}
          error={regenerate.isError ? regenerate.error : null}
          onCancel={() => setStep("idle")}
          onSubmit={(value) =>
            regenerate.mutate(
              { code: value },
              {
                onSuccess: (result) => {
                  setCodes(result.recoveryCodes);
                  setStep("codes");
                },
              },
            )
          }
        />
      ) : null}

      {step === "disable" && user.mfaEnabled ? (
        <CodeConfirmForm
          title="Desactivar la verificación en dos pasos"
          description="Se borrarán también tus códigos de recuperación. Confirma con un código de tu app o uno de recuperación."
          submitLabel="Desactivar"
          danger
          pending={disable.isPending}
          error={disable.isError ? disable.error : null}
          onCancel={() => setStep("idle")}
          onSubmit={(value) => disable.mutate({ code: value }, { onSuccess: () => setStep("idle") })}
        />
      ) : null}

      {step === "setup" ? (
        <form onSubmit={confirm} className="space-y-3 rounded-lg border border-ink-200 p-3 dark:border-ink-700">
          <ol className="list-decimal space-y-1 pl-5 text-xs text-ink-600 dark:text-ink-300">
            <li>Abre tu app de autenticación (Google Authenticator, Microsoft Authenticator, Authy, 1Password…).</li>
            <li>Escanea el código QR o introduce la clave a mano.</li>
            <li>Escribe aquí el código de 6 dígitos que muestra la app.</li>
          </ol>
          <div className="flex flex-wrap items-center gap-4">
            {qr ? (
              <img src={qr} alt="Código QR para la app de autenticación" className="h-[200px] w-[200px] rounded bg-white" />
            ) : (
              <p className="text-xs text-ink-500">Generando código QR…</p>
            )}
            <div className="min-w-0">
              <p className="micro-label">Clave manual</p>
              <code className="ident break-all text-xs">{secret}</code>
            </div>
          </div>
          <Field label="Código de 6 dígitos" htmlFor="mfa-enable-code">
            <Input
              id="mfa-enable-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              className="tracking-widest"
            />
          </Field>
          {enable.isError ? <ErrorNotice error={enable.error} /> : null}
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={enable.isPending || code.length !== 6}>
              {enable.isPending ? "Comprobando…" : "Activar"}
            </Button>
            <Button onClick={() => setStep("idle")}>Cancelar</Button>
          </div>
        </form>
      ) : null}

      {step === "idle" ? (
        user.mfaEnabled ? (
          <div className="space-y-2">
            <p className="text-xs text-ink-600 dark:text-ink-300">
              Códigos de recuperación disponibles: <strong>{remaining}</strong>
              {remaining <= 3 ? " — quedan pocos, conviene generar nuevos." : "."}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setStep("regenerate")}>Generar nuevos códigos</Button>
              <Button variant="danger" onClick={() => setStep("disable")}>
                Desactivar
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-ink-600 dark:text-ink-300">
              Opcional, pero muy recomendable si gestionas documentos o usuarios: además de la contraseña, se pedirá un
              código de tu móvil al entrar. Te daremos 10 códigos de recuperación por si lo pierdes.
            </p>
            {hasPin ? (
              <Notice tone="neutral">Tienes un PIN de acceso activo. Quítalo antes de activar la app de autenticación.</Notice>
            ) : (
              <Button variant="primary" onClick={start} disabled={setup.isPending}>
                {setup.isPending ? "Preparando…" : "Activar verificación en dos pasos"}
              </Button>
            )}
            {setup.isError ? <ErrorNotice error={setup.error} /> : null}
          </div>
        )
      ) : null}
    </section>
  );
}

// ── PIN de acceso (solo conductores) ─────────────────────────────────────────

function PinSection({ user }: { user: MeResponse }) {
  const setPin = useSetPin();
  const disablePin = useDisablePin();
  const [mode, setMode] = useState<"idle" | "set" | "remove">("idle");
  const [pin, setPinValue] = useState("");
  const [pin2, setPin2] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const reset = (): void => {
    setMode("idle");
    setPinValue("");
    setPin2("");
    setPassword("");
    setLocalError(null);
  };

  const submitSet = (event: FormEvent): void => {
    event.preventDefault();
    setLocalError(null);
    if (pin !== pin2) {
      setLocalError("Los dos PIN no coinciden.");
      return;
    }
    setPin.mutate(
      { pin, password },
      {
        onSuccess: () => {
          setDone(user.pinEnabled ? "PIN cambiado correctamente." : "PIN activado correctamente.");
          reset();
        },
      },
    );
  };

  const submitRemove = (event: FormEvent): void => {
    event.preventDefault();
    disablePin.mutate(
      { password },
      {
        onSuccess: () => {
          setDone("PIN desactivado.");
          reset();
        },
      },
    );
  };

  return (
    <section aria-labelledby="pin-title" className="space-y-3 border-t border-ink-200 pt-4 dark:border-ink-800">
      <div className="flex items-center justify-between">
        <h3 id="pin-title" className="text-sm font-semibold text-ink-800 dark:text-ink-100">
          PIN de acceso (conductores)
        </h3>
        {user.pinEnabled ? <Chip tone="ok">Activo</Chip> : <Chip tone="neutral">Sin activar</Chip>}
      </div>
      <p className="text-xs text-ink-600 dark:text-ink-300">
        Un PIN de 6 dígitos que se pide al entrar, además de la contraseña. Es más sencillo que la app de
        autenticación y sirve en la app del conductor. No uses fechas ni números repetidos o en secuencia.
      </p>
      {user.pinMustChange ? (
        <Notice tone="warn">Tu PIN es temporal: elige uno propio ahora para poder seguir usando la aplicación.</Notice>
      ) : null}
      {done ? <p className="text-xs text-signal-ok dark:text-teal-300">{done}</p> : null}

      {mode === "set" ? (
        <form onSubmit={submitSet} className="space-y-3 rounded-lg border border-ink-200 p-3 dark:border-ink-700">
          <Field label="Nuevo PIN (6 dígitos)" htmlFor="pin-new">
            <Input
              id="pin-new"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              required
              value={pin}
              onChange={(event) => setPinValue(event.target.value.replace(/\D/g, ""))}
              className="tracking-widest"
            />
          </Field>
          <Field label="Repite el PIN" htmlFor="pin-repeat">
            <Input
              id="pin-repeat"
              type="password"
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              required
              value={pin2}
              onChange={(event) => setPin2(event.target.value.replace(/\D/g, ""))}
              className="tracking-widest"
            />
          </Field>
          <Field label="Tu contraseña" htmlFor="pin-password" hint="Para confirmar que eres tú.">
            <Input
              id="pin-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
          {localError ? <p className="text-xs text-signal-danger dark:text-red-400">{localError}</p> : null}
          {setPin.isError ? <ErrorNotice error={setPin.error} /> : null}
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={setPin.isPending || pin.length !== 6 || !password}>
              {setPin.isPending ? "Guardando…" : "Guardar PIN"}
            </Button>
            <Button onClick={reset}>Cancelar</Button>
          </div>
        </form>
      ) : null}

      {mode === "remove" ? (
        <form onSubmit={submitRemove} className="space-y-3 rounded-lg border border-ink-200 p-3 dark:border-ink-700">
          <Field label="Tu contraseña" htmlFor="pin-remove-password" hint="Para confirmar que quieres quitar el PIN.">
            <Input
              id="pin-remove-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
          {disablePin.isError ? <ErrorNotice error={disablePin.error} /> : null}
          <div className="flex gap-2">
            <Button type="submit" variant="danger" disabled={disablePin.isPending || !password}>
              {disablePin.isPending ? "Quitando…" : "Quitar PIN"}
            </Button>
            <Button onClick={reset}>Cancelar</Button>
          </div>
        </form>
      ) : null}

      {mode === "idle" ? (
        user.mfaEnabled ? (
          <Notice tone="neutral">Tienes la app de autenticación activa. Desactívala antes de usar un PIN.</Notice>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={() => {
                setDone(null);
                setMode("set");
              }}
            >
              {user.pinEnabled ? "Cambiar PIN" : "Crear PIN"}
            </Button>
            {user.pinEnabled ? (
              <Button
                variant="danger"
                onClick={() => {
                  setDone(null);
                  setMode("remove");
                }}
              >
                Quitar PIN
              </Button>
            ) : null}
          </div>
        )
      ) : null}
    </section>
  );
}

// ── Tarjeta completa ─────────────────────────────────────────────────────────

export function SecurityPanel({ user }: { user: MeResponse }) {
  return (
    <Card>
      <h2 className="micro-label mb-3">Verificación en dos pasos</h2>
      <div className="space-y-4">
        <MfaSection user={user} />
        {user.canUsePin ? <PinSection user={user} /> : null}
      </div>
    </Card>
  );
}
